<template>
    <div class="custom-select-wrapper" :class="`custom-select-wrapper--theme--${theme}`">
        <span
            v-if="label && isLabelAsideLeft"
            class="input-label custom-select__label--aside-left"
            @click="open = !open"
        >
            {{ label }}
        </span>
        <div
            class="custom-select"
            :class="{'custom-select--bordered': hasBorder}"
            :tabindex="tabindex"
            @blur="open = false"
        >
            <span
                v-if="label && !isLabelAsideLeft"
                class="custom-select__label"
                @click="open = !open"
            >
                {{ label }}
            </span>
            <div
                class="custom-select__value"
                :class="{open: open}"
                @click="open = !open"
            >
                <slot name="prefix"></slot>
                {{ selected }}
                <svg
                    class="custom-select__arrow"
                    :class="{'arrow--hidden': !isDropdownArrowShown}"
                    width="14"
                    height="8"
                    viewBox="0 0 14 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M2 0L7 5L12 0L14 1L7 8L0 1L2 0Z" />
                </svg>
            </div>
            <ul
                class="custom-select__options-list"
                :class="{hidden: !open}"
            >
                <li
                    class="custom-select__option"
                    v-for="(option, i) in options"
                    :key="i"
                    @click="
                        selected = option.text;
                        open = false;
                        $emit('input', option);
                    "
                >
                    <span class="custom-select__option-title">{{ option.text }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'CustomSelect',
        props: {
            options: {
                type: Array,
                required: true,
            },
            default: {
                type: Object,
                required: false,
                default: null,
            },
            tabindex: {
                type: Number,
                required: false,
                default: 0,
            },
            hasBorder: {
                type: Boolean,
                required: false,
                default: false,
            },
            isDropdownArrowShown: {
                type: Boolean,
                required: false,
                default: true,
            },
            label: {
                type: String,
                default: '',
            },
            isLabelAsideLeft: {
                type: Boolean,
                default: false,
            },
            theme: {
                type: String,
                default: 'dark',
            },
        },
        data() {
            return {
                selected: this.default.text
                        ? this.default.text
                        : this.options.length > 0
                        ? this.options[0]
                        : null,
                    open: false,
            };
        },
        mounted() {
            this.$emit('input', this.default);
        },
    };
</script>

<style lang="scss" scoped>
    @import "~assets/css/_vars.scss";

    .custom-select-wrapper {
        width: 100%;
        display: flex;
        align-items: flex-end;
        position: relative;
        min-height: 78px;
    }

    .custom-select {
        border-radius: $inputsBorderRadius;
        width: 100%;
        min-width: 95px;
        position: relative;
        height: 48px;
        min-height: 48px;
        &:focus {
            outline: 1px solid rgba(255, 255, 255, 0.55);
        }
        &__label {
            position: absolute;
            top: -25px;
            left: 0;
            cursor: pointer;
            &--aside-left {
                display: flex;
                justify-content: flex-end;
                min-width: 95px;
                cursor: pointer;
                margin-right: 20px;
            }
        }
        &__value {
            padding: 15px 20px;
            cursor: pointer;
            font-weight: bold;
            font-size: $mainFontSize;
            line-height: 19px;
            text-transform: uppercase;
        }
        &--bordered {
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
        &__arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 20px;
            margin-left: 5px;
            transition: $transitionAllFast;
            cursor: pointer;
            fill: $accentColor;
            &--hidden {
                display: none;
            }
        }
        .open &__arrow {
            transform: rotate(180deg) translateY(50%);
            transition: $transitionAllFast;
            // fill: $mainLightFontColor;
            opacity: 1;
        }
        &__options-list {
            border-radius: $inputsBorderRadius;
            position: absolute;
            top: 110%;
            left: 0;
            width: 100%;
            max-height: 220px;
            list-style: none;
            overflow-x: hidden;
            overflow-y: auto;
            margin: 0;
            padding: 15px 20px;
            background-color: #272B33;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
            z-index: 50;
            transition: $transitionAllFast;
            &.hidden {
                max-height: 0;
                visibility: hidden;
            }
            /* Scrollbar custom styles */
            &::-webkit-scrollbar {
                width: 3px;
            }

            &::-webkit-scrollbar-track {
                border-radius: 2px;
            }

            &::-webkit-scrollbar-track-piece {
                background-color: inherit;
            }

            &::-webkit-scrollbar-thumb:vertical {
                width: 3px;
                min-height: 60px;
                background-color: rgba(255, 255, 255, 0.1);
                border-radius: 2px;
            }

            /* Scrollbar custom styles for Firefox */
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.1) #272B33;
        }
        &__option {
            display: flex;
            height: 30px;
            align-items: center;
        }
        &__option-title {
            margin-left: 15px;
            font-weight: normal;
            font-size: $mainFontSize;
            line-height: 19px;
            &:hover {
                color: $accentColor;
                cursor: pointer;
            }
        }
    }

    .custom-select-wrapper--theme--dark {
        .custom-select {
            color: $mainLightFontColor;
            &__options-list {
                background-color: #272B33;
            }
            .open &__arrow {
                fill: $mainLightFontColor;
            }
        }
    }

    .custom-select-wrapper--theme--light {
        .custom-select {
            border: 1px solid #BABABA;
            background-color: #fff;
            color: #000000;
            &__label {
                color: #000000;
            }
            .open &__arrow {
                fill: $accentColor;
            }
            &__options-list {
                background-color: #BABABA;
                box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
            }
        }
    }


    @media (max-width: $breakpoint-tablet) {
        .custom-select {
            &__option {
                padding-bottom: 15px;
                &:last-child {
                    padding-bottom: 0;
                }
            }
        }
    }

    @media (max-width: $breakpoint-phone) {
        .custom-select{
            &__options-list {
                padding: 10px;
            }
            &__option-title {
                margin-left: 10px;
            }
            &__option {
                padding-bottom: 15px;
                &:last-child {
                    padding-bottom: 0;
                }
            }
        }
    }
</style>
